import { animate, style, transition, trigger } from '@angular/animations';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { LangChangeEvent, TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { MyMBMediaModalComponent } from '@modules/shared/modal/my-mb-media-modal/my-mb-media-modal..component';
import { UserProfileModalComponent } from '@modules/shared/modal/user-profile-modal/user-profile-modal.component';
import { User } from '@modules/shared/models/user.model';
import { HeaderService } from '@modules/shared/services/header.service';
import { LocaleService } from '@modules/shared/services/locale.service';
import { ModalService } from '@modules/shared/services/modal.service';
import { UserService } from '@modules/shared/services/user.service';
import { languageConfig } from 'src/environments/language.config';

import { HeaderSearchInputFieldComponent } from '../header-search-input-field/header-search-input-field.component';

@Component({
  selector: 'mb-header-bar',
  templateUrl: './header-bar.component.html',
  styleUrls: ['./header-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('.3s cubic-bezier(0.25, 0.1, 0.25, 1)', style({ opacity: 1 })),
      ]),
    ]),
  ],
  imports: [RouterLink, NgTemplateOutlet, HeaderSearchInputFieldComponent, AsyncPipe, TranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HeaderBarComponent {
  private translateService = inject(TranslateService);
  private localeService = inject(LocaleService);
  private userService = inject(UserService);
  private headerService = inject(HeaderService);
  private modalService = inject(ModalService);
  private router = inject(Router);

  public user$: Observable<User | null>;
  public isSearchInputFieldClosed = true;
  public currentLanguage = this.translateService.getCurrentLang();

  public languages = languageConfig.languages.map((isoLang) => ({
    isoLang,
    label: isoLang.split('-')[0].toUpperCase(),
  }));

  @Input() notificationCount: number = 0;

  constructor() {
    this.user$ = this.userService.user$;
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.currentLanguage = event.lang;
    });
  }

  public login(): void {
    this.headerService.closeMenu();
    this.userService.login();
  }

  public logout(): void {
    this.userService.logout();
  }

  public changeLanguage(language: string): void {
    this.localeService.changeLanguage(language);
  }

  public closeMenu() {
    this.headerService.closeMenu();
  }

  public openProfileModal(): void {
    this.headerService.closeMenu();
    this.modalService.open(UserProfileModalComponent);
  }

  public toggleSearchInputField(value: boolean): void {
    if (!value) {
      this.headerService.closeMenu();
    }
    this.isSearchInputFieldClosed = value;
  }

  public openMyContent(): void {
    this.headerService.closeMenu();
    this.modalService.open(MyMBMediaModalComponent);
  }

  public toggleNavigationMenu(): void {
    if (this.headerService.isMenuOpen) {
      this.headerService.closeMenu();
    } else {
      this.headerService.openMenu({ level: 1 });
    }
  }

  public openImprint(event: Event) {
    this.headerService.closeMenu();
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate(['/legal/provider'], {
      state: { scrollRestoration: 'top' },
    });
  }

  public openPressContacts(event: Event) {
    this.headerService.closeMenu();
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate(['presscontacts']);
  }
}
